import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useUserRoles } from '@/hooks/useUserRoles';
import { FreemiumGuard } from '@/components/FreemiumGuard';
import { PomodoroProvider } from '@/contexts/PomodoroContext';
import { FloatingPomodoroWidget } from '@/components/FloatingPomodoroWidget';
import { supabase } from '@/integrations/supabase/client';
import brandLogoHorizontal from '@/assets/brand/nivela-logo-horizontal.png';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, userStatus } = useAuth();
  const { status, loading: billingLoading } = useBillingStatus();
  const { isDeveloper, isSuperAdmin } = useUserRoles();
  const location = useLocation();

  const [mustChange, setMustChange] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setMustChange(null);
      return;
    }
    supabase
      .from('profiles')
      .select('must_change_password')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setMustChange(!!data?.must_change_password);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  if (loading || billingLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <img src={brandLogoHorizontal} alt="Nível A" className="h-10 w-auto animate-pulse" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (userStatus && userStatus !== 'approved') {
    return <Navigate to="/auth" replace />;
  }

  // Força troca de senha se a flag estiver ligada (e o usuário não estiver
  // já na página /reset-password).
  if (mustChange === true && !location.pathname.startsWith('/reset-password')) {
    return <Navigate to="/reset-password?force=1" replace />;
  }

  // Gate de suspensão por inadimplência: apenas Proprietário (super_admin) e
  // DEVs (developer) podem entrar em /admin/billing para regularizar.
  if (status === 'suspended') {
    const isOwnerOrDev = isSuperAdmin || isDeveloper;
    const onAdminBilling = location.pathname.startsWith('/admin/billing');
    if (!(isOwnerOrDev && onAdminBilling)) {
      return <Navigate to="/auth" replace />;
    }
  }

  return (
    <PomodoroProvider>
      <FreemiumGuard>{children}</FreemiumGuard>
      <FloatingPomodoroWidget />
    </PomodoroProvider>
  );
}
